import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  AggFunc,
  BaseComponent,
  BaseEditFormComponent,
  FormatService,
  GridRowSelect,
  GridService,
  PurchaseOrderLineSalesAllocationsSummaryTop,
  PurchaseOrderLineService,
  PurchaseOrderSalesAllocation,
  SalesOrderLinePurchaseAllocationsSummaryTop,
  SalesOrderLineService,
  SummaryItem,
} from 'tms-lib';
import { PurchaseOrderSalesAllocationService, PurchaseOrderService } from '../../../services';

@Component({
  selector: 'app-purchase-order-line-sales-order-allocation',
  templateUrl: './purchase-order-line-sales-order-allocation.component.html',
  styleUrl: './purchase-order-line-sales-order-allocation.component.scss',
})
export class PurchaseOrderLineSalesOrderAllocationComponent extends BaseComponent implements OnInit {
  @ViewChild(BaseEditFormComponent) form: BaseEditFormComponent;
  @Input() purchaseOrderLineId: string;
  @Input() header: string;
  @Input() gridCssClass = 'tab-grid';
  @Output() modelUpdated = new EventEmitter();

  model$: Observable<SalesOrderLinePurchaseAllocationsSummaryTop>;
  salesOrderLines$: Observable<PurchaseOrderSalesAllocation[]>;

  columnDefs: ColDef[] = [
    ...this.gridService.checkboxRowSelect('salesOrderAndLineNo', $localize`Sales Order Number`, undefined, undefined, {
      abbreviatedHeaderName: 'SO Number',
    }),
    this.gridService.text('customerKey', $localize`Customer`),
    this.gridService.text('customerName', $localize`Name`),
    this.gridService.text('balance', $localize`Current Quantity`, { aggFunc: AggFunc.sum }),
    this.gridService.date('deliveryDate', $localize`Delivery Date`),
    this.gridService.numberFormat('totalAllocated', $localize`Total Allocated`, { aggFunc: AggFunc.sum }),
    this.gridService.editableField(
      'allocated',
      $localize`This Allocation`,
      (model: PurchaseOrderSalesAllocation) => this.allocatedEdited(model),
      (model: PurchaseOrderSalesAllocation) => +model.allocated > 0,
      { aggFunc: AggFunc.sum },
    ),
    this.gridService.text('deliveryAddressName', $localize`Delivery Name`),
    this.gridService.text('deliveryAddress', $localize`Delivery Address`),
  ];

  constructor(
    private purchaseOrderLineService: PurchaseOrderLineService,
    private salesOrderLineService: SalesOrderLineService,
    private purchaseOrderSalesAllocationService: PurchaseOrderSalesAllocationService,
    private purchaseOrderService: PurchaseOrderService,
    private gridService: GridService,
    private formatService: FormatService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.getModel();
  }

  summaryItems(model: PurchaseOrderLineSalesAllocationsSummaryTop): SummaryItem[] {
    return [
      { label: $localize`Product`, value: model.productKey },
      { label: $localize`Description`, value: model.productDescription },
      { label: $localize`Quantity`, value: model.quantity },
      { label: $localize`Allocated`, value: model.allocated },
      { label: $localize`Delivery Date`, value: this.formatService.date(model.deliveryDate) },
    ];
  }

  getModel() {
    this.model$ = this.purchaseOrderLineService.salesAllocationsSummaryTop(this.purchaseOrderLineId);
    this.salesOrderLines$ = this.salesOrderLineService.purchaseOrderSalesAllocation(this.purchaseOrderLineId);
  }

  updateSuccess() {
    this.getModel();
    this.modelUpdated.emit();

    this.purchaseOrderService.refreshDetails.next();
    this.purchaseOrderLineService.refreshDetails.next();
    this.purchaseOrderSalesAllocationService.refreshDetails.next();
  }

  rowSelected(event: GridRowSelect) {
    const allocateOrDeallocate = event.selected
      ? this.purchaseOrderSalesAllocationService.allocate(this.purchaseOrderLineId, event.model.salesOrderLineId)
      : this.purchaseOrderSalesAllocationService.delete(event.model.id);

    allocateOrDeallocate.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateSuccess());
  }

  private allocatedEdited(model) {
    this.purchaseOrderSalesAllocationService
      .updatePurchaseOrderSalesAllocation(model.id, model.allocated)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateSuccess());
  }
}
